import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, Clock, Lock, Award, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { TESTIMONIALS, SAMPLES } from "@/data/stories";
import { MaskedLines, Reveal, Eyebrow } from "@/components/Motion";
import { SERVICES } from "@/data/services";
import { STATS, whatsappWithText } from "@/lib/site";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const HERO_IMG =
  "https://images.pexels.com/photos/16420237/pexels-photo-16420237.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TRUST = [
  { icon: ShieldCheck, title: "100% Original", text: "Every document is written from scratch and verified with a plagiarism report." },
  { icon: Clock, title: "On-Time Delivery", text: "Milestone-based schedules so your submission date is never at risk." },
  { icon: Lock, title: "Strict Confidentiality", text: "Your identity, institution, and documents stay private. Always." },
  { icon: Award, title: "Subject-Matter Experts", text: "Writers with advanced degrees across business, law, and technical fields." },
];

const CHAPTERS = [
  { n: "01", title: "Brief us", text: "Share your topic, guidelines, and deadline on WhatsApp. We respond with questions, not assumptions." },
  { n: "02", title: "Get a plan", text: "A scoped milestone schedule and a fixed quote — no hidden costs, no vague promises." },
  { n: "03", title: "Review drafts", text: "Work arrives chapter by chapter with revision windows built in, so you stay in control." },
  { n: "04", title: "Submit with proof", text: "Final delivery includes formatting to your university's rules and a plagiarism report." },
];

const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 90]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.08]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#4F46E5] text-white">
      <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 -right-24 h-80 w-80 rounded-full bg-indigo-300/20 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pt-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-white/90 font-semibold mb-6"
          >
            Trusted by students across India, Europe &amp; beyond
          </motion.p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            <MaskedLines
              lines={[
                "Get your thesis",
                <>done <span className="text-amber-300">right,</span></>,
                "on time. Every time.",
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 text-base md:text-lg text-white/80 leading-relaxed max-w-xl"
          >
            MBA projects, theses, dissertations, and research papers — written from scratch for you, checked for plagiarism, and formatted exactly the way your university wants.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={whatsappWithText("Hi, I'd like a free quote for my project/thesis.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-green-500/25 transition-transform duration-200 hover:-translate-y-0.5"
            >
              <WhatsAppIcon className="h-5 w-5" /> Get a Free Quote
            </a>
            <Link
              to="/services"
              data-testid="hero-services-link"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/20"
            >
              Explore Services <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-12 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 pr-2 sm:pr-0"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-4 py-4">
                <p className="font-display font-extrabold text-2xl md:text-3xl leading-none">{s.value}</p>
                <p className="mt-1.5 text-[11px] md:text-xs text-white/70 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl border border-white/20 shadow-2xl shadow-blue-900/40">
              <motion.img
                src={HERO_IMG}
                alt="Student researching in a university library"
                style={{ y, scale }}
                className="w-full h-[440px] object-cover"
              />
            </div>
            <div className="absolute -top-4 -right-4 rounded-2xl bg-amber-300 text-[#1E2A78] px-5 py-3 shadow-lg rotate-2">
              <p className="font-display font-extrabold text-sm leading-tight">100% Plagiarism-Free</p>
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white text-[#1E2A78] rounded-2xl px-6 py-4 shadow-xl">
              <p className="font-display font-extrabold text-2xl leading-none">8+</p>
              <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest">Specialised services</p>
            </div>
          </motion.div>
        </div>
      </div>
      <svg className="absolute bottom-0 left-0 w-full h-8 md:h-12 text-white" viewBox="0 0 1440 60" fill="currentColor" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0,32 C360,72 1080,-8 1440,32 L1440,60 L0,60 Z" />
      </svg>
    </section>
  );
};

const MarqueeStrip = () => (
  <div className="bg-[#F5F8FF] border-b border-slate-200 py-4" data-testid="editorial-marquee">
    <Marquee speed={30} gradient={false} autoFill>
      {["Plagiarism-Free", "Confidential", "On-Time Delivery", "APA · MLA · Harvard · OSCOLA", "Chapter-wise Review", "Free Quote in Hours"].map((t) => (
        <span key={t} className="mx-10 text-xs uppercase tracking-[0.3em] text-slate-400 font-medium">
          {t} <span className="ml-10 text-slate-300">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const ServicesCarousel = () => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 380, behavior: "smooth" });
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <Eyebrow>What we do</Eyebrow>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
            Eight specialised services. One standard: submission-ready.
          </h2>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={() => scroll(-1)}
            data-testid="services-carousel-prev"
            aria-label="Previous services"
            className="h-11 w-11 rounded-full border border-slate-300 flex items-center justify-center text-[#1E2A78] transition-colors duration-200 hover:bg-[#1E2A78] hover:text-white"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <button
            onClick={() => scroll(1)}
            data-testid="services-carousel-next"
            aria-label="More services"
            className="h-11 w-11 rounded-full border border-slate-300 flex items-center justify-center text-[#1E2A78] transition-colors duration-200 hover:bg-[#1E2A78] hover:text-white"
          >
            <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div ref={ref} className="mt-12 flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2" data-testid="services-carousel">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group snap-start shrink-0 w-[290px] md:w-[340px] bg-white border border-slate-200 rounded-2xl overflow-hidden transition-[box-shadow,border-color] duration-300 hover:shadow-xl hover:border-slate-300 flex flex-col"
            >
              <div className="overflow-hidden h-44">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
                  <s.icon className="h-5 w-5 text-[#2563EB]" strokeWidth={1.5} />
                </span>
                <h3 className="mt-4 font-display font-bold text-lg tracking-tight">{s.name}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#1E2A78]">
                  Learn more
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const SamplesSection = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
    <Reveal>
      <Eyebrow>Sample work</Eyebrow>
      <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
        Judge the writing, not the promises.
      </h2>
      <p className="mt-4 text-slate-600 max-w-xl">
        Anonymised excerpts from delivered work. Names, institutions, and identifying details removed to protect client confidentiality.
      </p>
    </Reveal>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
      {SAMPLES.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.08} className="h-full">
          <div className="flex flex-col h-full bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`sample-card-${i}`}>
            <div className="overflow-hidden h-40">
              <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-7 flex flex-col flex-1">
              <span className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-medium">{s.tag}</span>
              <h3 className="mt-3 font-display font-bold text-lg tracking-tight">{s.title}</h3>
              <p className="mt-4 text-sm text-slate-500 leading-relaxed italic border-l-2 border-slate-200 pl-4 flex-1">
                {s.excerpt}
              </p>
              <p className="mt-5 text-xs text-slate-400 uppercase tracking-widest">Anonymised excerpt</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const FAQS = [
  {
    q: "How is pricing decided?",
    a: "Pricing is based on your academic level and what the work needs — length, deadline, research depth, and analysis. Tiers start at ₹4,999, and you always receive a fixed quote before any work begins — no surprises mid-project.",
  },
  {
    q: "Is my identity kept confidential?",
    a: "Completely. Your name, university, and documents are never shared, published, or reused. We don't display real client names anywhere, and all communication stays between you and your assigned team.",
  },
  {
    q: "How many revisions do I get?",
    a: "Every package includes revision rounds — one on Basic, two on Standard, and unlimited scoped revisions on Premium. Revisions within the agreed scope are always free.",
  },
  {
    q: "Will the work pass plagiarism checks?",
    a: "Yes. Everything is written from scratch and run through plagiarism-detection software before delivery. You receive the similarity report with your document, and if your institution flags anything we wrote, we rewrite it free.",
  },
  {
    q: "Can you handle urgent deadlines?",
    a: "Often, yes. Short pieces can be turned around in 24–72 hours and express options exist for larger work. Message us on WhatsApp with your deadline and we'll tell you honestly whether it's feasible.",
  },
  {
    q: "Do you format for my university's guidelines?",
    a: "Always. Send us your institution's handbook or template and we format margins, citations, front matter, and chapter structure to match — whether you study in India, Europe, North America, or the Middle East.",
  },
];

const FaqSection = () => (
  <section className="bg-[#F5F8FF] border-y border-slate-200" data-testid="faq-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4">
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
          Questions students ask before they message us.
        </h2>
        <p className="mt-4 text-slate-600 text-sm leading-relaxed">
          Something else on your mind? WhatsApp us — a real person replies, usually within hours.
        </p>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-8">
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-slate-200">
              <AccordionTrigger className="text-left font-display font-bold text-base md:text-lg tracking-tight hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-slate-600 leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const TrustSection = () => (
  <section className="bg-[#F5F8FF] border-y border-slate-200">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <Eyebrow>Why students trust us</Eyebrow>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-2xl">
          Built on four non-negotiables.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
        {TRUST.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.08} className="bg-white p-8 h-full" data-testid={`trust-badge-${i}`}>
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
              <t.icon className="h-5 w-5 text-[#2563EB]" strokeWidth={1.5} />
            </span>
            <h3 className="mt-5 font-display font-bold text-base tracking-tight">{t.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProcessChapters = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
    <Reveal>
      <Eyebrow>How it works</Eyebrow>
      <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
        A process you can see, at every step.
      </h2>
    </Reveal>
    <div className="mt-16 space-y-0 divide-y divide-slate-200 border-y border-slate-200">
      {CHAPTERS.map((c, i) => (
        <Reveal key={c.n} delay={i * 0.05}>
          <div className="grid grid-cols-12 items-center gap-6 py-10">
            <span className="col-span-3 md:col-span-2 font-display font-extrabold text-5xl md:text-7xl text-blue-200 leading-none">
              {c.n}
            </span>
            <h3 className="col-span-9 md:col-span-3 font-display font-bold text-xl md:text-2xl tracking-tight">{c.title}</h3>
            <p className="col-span-12 md:col-span-7 text-sm md:text-base text-slate-500 leading-relaxed">{c.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Testimonials = () => {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      const el = ref.current;
      if (paused || !el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 40;
      if (atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: 372, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(id);
  }, [paused]);

  const scroll = (d) => ref.current?.scrollBy({ left: d * 372, behavior: "smooth" });

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#4F46E5] text-white" data-testid="testimonials-slider">
      <div className="absolute -top-24 right-1/4 h-72 w-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Eyebrow light>What students say</Eyebrow>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight max-w-xl">
              900+ students helped. Here's what a few of them told us.
            </h2>
            <Link
              to="/success-stories"
              data-testid="view-all-stories-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-amber-300 hover:text-amber-200 transition-colors duration-200"
            >
              View all success stories <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="flex gap-3 shrink-0">
            <button
              onClick={() => scroll(-1)}
              data-testid="testimonials-prev"
              aria-label="Previous testimonials"
              className="h-11 w-11 rounded-full border border-white/30 flex items-center justify-center text-white transition-colors duration-200 hover:bg-white hover:text-[#2563EB]"
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => scroll(1)}
              data-testid="testimonials-next"
              aria-label="More testimonials"
              className="h-11 w-11 rounded-full border border-white/30 flex items-center justify-center text-white transition-colors duration-200 hover:bg-white hover:text-[#2563EB]"
            >
              <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </Reveal>
        <div
          ref={ref}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="mt-12 flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2"
        >
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonial-${i}`}
              className="snap-start shrink-0 w-[320px] md:w-[352px] rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-7 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <Quote className="h-6 w-6 text-amber-300" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-widest bg-white/10 border border-white/15 rounded-full px-3 py-1 text-white/80">
                  {t.region}
                </span>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-white/90 flex-1">{t.quote}</p>
              <div className="mt-6">
                <p className="font-display font-bold text-base">{t.name}</p>
                <p className="text-xs text-white/60 mt-0.5">{t.meta}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center sm:text-left">
    <Reveal className="relative overflow-hidden rounded-3xl p-10 md:p-16 bg-gradient-to-br from-[#1D4ED8] via-[#2563EB] to-[#4F46E5] text-white shadow-2xl shadow-blue-500/20">
      <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <h2 className="relative font-display font-extrabold text-3xl md:text-5xl tracking-tight max-w-3xl">
        Deadline approaching? Send your brief now.
      </h2>
      <p className="relative mt-5 text-base md:text-lg text-white/80 max-w-xl">
        Share your guidelines on WhatsApp and receive a scoped plan with a fixed quote — usually within a few hours.
      </p>
      <div className="relative mt-8 flex flex-wrap gap-4 justify-center sm:justify-start">
        <a
          href={whatsappWithText("Hi, my deadline is close. Can you help with my project?")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="final-cta-whatsapp"
          className="inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-green-600/25 transition-transform duration-200 hover:-translate-y-0.5"
        >
          <WhatsAppIcon className="h-5 w-5" /> WhatsApp Us Now
        </a>
        <Link
          to="/pricing"
          data-testid="final-cta-pricing"
          className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#2563EB] transition-transform duration-200 hover:-translate-y-0.5"
        >
          View Pricing <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </Link>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="MBA Project & Thesis Writing Help | Plagiarism-Free Academic Writing Services"
        description="Custom MBA projects, theses, dissertations, and research papers for students at Indian, European, US, Canadian, and Middle East universities. 100% original, on-time, confidential."
      />
      <HeroSection />
      <MarqueeStrip />
      <ServicesCarousel />
      <TrustSection />
      <ProcessChapters />
      <SamplesSection />
      <Testimonials />
      <FaqSection />
      <FinalCTA />
    </div>
  );
}
